import { ChevronDown, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUpdateVideoApproval } from "@/hooks/use-videos";
import { APPROVAL_STATUS_LABELS, type ApprovalStatus, type Video } from "@/lib/api-types";
import { cn } from "@/lib/utils";

const ORDER: ApprovalStatus[] = ["in_review", "changes_requested", "approved"];

const DOT: Record<ApprovalStatus, string> = {
  in_review: "bg-amber-500",
  changes_requested: "bg-destructive",
  approved: "bg-emerald-500",
};

type Props = {
  projectId: string;
  video: Video;
};

/**
 * REAL — PATCH /api/editor/videos/:id/approval. Matches the reference
 * screenshot's Status dropdown, minus the search box (explicitly dropped —
 * 3 fixed options don't need search). Any workspace role can change this
 * (CHANGE_APPROVAL_STATUS is granted to all 4 tiers).
 */
export function VideoStatusControl({ projectId, video }: Props) {
  const updateApproval = useUpdateVideoApproval(projectId);
  const current = video.approvalStatus ?? "in_review";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={updateApproval.isPending}
          className="flex w-full items-center gap-2 rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs text-foreground transition-colors hover:bg-secondary/60 disabled:opacity-60"
        >
          <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", DOT[current])} />
          <span className="flex-1 text-left">
            {updateApproval.isPending ? "Updating…" : APPROVAL_STATUS_LABELS[current]}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {ORDER.map((status) => (
          <DropdownMenuItem
            key={status}
            onSelect={() => {
              if (status !== current) updateApproval.mutate({ videoId: video.id, approvalStatus: status });
            }}
          >
            <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", DOT[status])} />
            {APPROVAL_STATUS_LABELS[status]}
            {status === current && <Check className="ml-auto h-3.5 w-3.5 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
